// React Dependencies
import React, { useState, useEffect, useCallback } from 'react';

// External Dependencies
import PropTypes from 'prop-types';
import _ from 'lodash';
import { FormLabel, makeStyles, Typography, TextField } from '@material-ui/core';
import { Input } from 'reactstrap';
import CancelOutlinedIcon from '@material-ui/icons/CancelOutlined';
import Select from 'react-select';

// Internal Dependencies
import PillButton from '../../common/Button/PillButton';
import { useMallMapStore } from '../../../hook/useMallMapStore';
import Constants, { Status } from '../../../common/constants';
import { IsEmpty } from '../../../common/checks';
import { Sleep } from '../../../common/util';
import apiService from '../../../services.js/apiService';

const useStyles = makeStyles(() => ({
  selectContainer: {
    zIndex:    10,
    '& label': {
      fontSize: '16px',
    },
  },
  selectInput: {
    '& div': {
      paddingTop:    '3px',
      paddingBottom: '3px',
    },
  },
  textInput: {
    width: '100%',

    '& input': {
      padding: '10px',
    },
  },
}));

const selectStyles = {
  menu: (provided) => ({
    ...provided,
    zIndex: 20,
  }),
};

const typeListOptions = _.map(
  Object.values(Constants.WaypointType),
  (waypoint) => ({ value: waypoint, label: waypoint }),
);

const MallNavigationForm = ({
  selectedWaypoint,
  pageListOptions,
  mallDirectoryListOptions,
  onUpdateWaypoint,
  onDeleteWaypoint,
}) => {
  const classes = useStyles();
  const { updateLogo, isWaypointTextVisible, selectedWaypointLabel, setUpdateLogo, setSelectedWaypoint, setSelectedWaypointLabel } = useMallMapStore((state) => state);

  const [type, setType] = useState(null);
  const [page, setPage] = useState(null);
  const [outlet, setOutlet] = useState(null);
  const [outletListOptions, setOutletListOptions] = useState([]);
  const [mallDirectory, setMallDirectory] = useState(null);
  const [textRotation, setTextRotation] = useState(0);

  // Format
  const formatOption = (optionValue, values = {}) => ({ ...values, value: optionValue, label: optionValue });
  const formatListOptions = (list, key) => _.map(list, (item) => ({ ...item, value: item[key], label: item[key] }));

  const closeFormHandler = () => {
    setSelectedWaypoint(null);
    setSelectedWaypointLabel(null);
  };

  const changeTypeSelectHandler = async (option) => {
    const updatedWaypoint = { ...selectedWaypoint, type: option.value, logo: null };

    setType(option);
    await onUpdateWaypoint(selectedWaypoint.id, updatedWaypoint);

    // Update logo
    await Sleep(100);
    setUpdateLogo(!updateLogo);
  };

  const changeMallDirectorySelectHandler = async (option) => {
    const updatedWaypoint = {
      ...selectedWaypoint,
      mallDirectoryId: option ? option.id : null,
      brandLogo:       option.brandLogo,
      name:            option.brand,
      unitNo:          option.unitNo,
    };

    setMallDirectory(option);
    await onUpdateWaypoint(selectedWaypoint.id, updatedWaypoint, true);

    // Update logo if exist
    await Sleep(100);
    setUpdateLogo(!updateLogo);
  };

  const changePageSelectHandler = async (option) => {
    const updatedWaypoint = { ...selectedWaypoint, outletPageId: option ? option.id : null, outletId: null };

    setPage(option);
    await onUpdateWaypoint(selectedWaypoint.id, updatedWaypoint);

    // Flush logo
    await Sleep(100);
    setUpdateLogo(!updateLogo);

    // Get list outlet
    setOutlet(null);
    if (option) {
      const outlets = await apiService.getPageOutlets(option.id, { status: Status.Live });
      const formattedOutlets = formatListOptions(outlets, 'name');
      setOutletListOptions(formattedOutlets);
    } else {
      setOutletListOptions([]);
    }
  };

  const changeOutletSelectHandler = (option) => {
    const updatedWaypoint = { ...selectedWaypoint, outletId: option ? option.id : null };

    setOutlet(option);
    onUpdateWaypoint(selectedWaypoint.id, updatedWaypoint);
  };

  const onBlurTextRotationHandler = () => {
    if (!IsEmpty(selectedWaypointLabel)) {
      setSelectedWaypointLabel(null);
    }
  };

  /**
   * Call network request after 400 miliseconds, when the user has stopped chaning the input
   */
  const debounceUpdateTextRotation = useCallback(_.debounce(() => {
    if (!IsEmpty(selectedWaypointLabel)) {
      onUpdateWaypoint(selectedWaypointLabel.id, selectedWaypointLabel);
    }
  }, 400), [textRotation]);

  const changeTextRotationHandler = (value) => {
    const updatedWaypoint = { ...selectedWaypoint, textRotation: Number(value) };
    setTextRotation(value);
    setSelectedWaypointLabel(updatedWaypoint);

    debounceUpdateTextRotation();
  };

  useEffect(() => {
    debounceUpdateTextRotation();

    // Cancel previous debounce calls during useEffect cleanup.
    return debounceUpdateTextRotation.cancel;
  }, [textRotation, debounceUpdateTextRotation]);

  const updateWayPointData = async (waypoint) => {
    // Update States
    setType(formatOption(waypoint.type));
    setTextRotation(waypoint.textRotation);

    // Update Page & Outlets States
    setPage(null);
    setOutletListOptions([]);
    setOutlet(null);

    if (!IsEmpty(waypoint.outletPageId)) {
      const foundPage = pageListOptions.find((item) => item.id === waypoint.outletPageId);
      setPage(formatOption(foundPage.name, foundPage));

      const outlets = await apiService.getPageOutlets(waypoint.outletPageId, { status: Status.Live });
      const formattedOutlets = formatListOptions(outlets, 'name');
      setOutletListOptions(formattedOutlets);

      const foundOutlet = formattedOutlets.find((item) => item.id === waypoint.outletId);
      if (!IsEmpty(foundOutlet)) {
        setOutlet(formatOption(foundOutlet.name, foundOutlet));
      }
    }

    // Update MallDirectory State
    setMallDirectory(null);

    if (!IsEmpty(waypoint.mallDirectoryId)) {
      const foundMallDirectory = mallDirectoryListOptions.find((item) => item.id === waypoint.mallDirectoryId);
      setMallDirectory(formatOption(foundMallDirectory.brand, foundMallDirectory));
    }
  };

  useEffect(() => {
    updateWayPointData(selectedWaypoint);
  }, [selectedWaypoint.id]);

  return (
    <>
      <div className="d-flex align-items-center justify-content-between">
        <Typography variant="h6">Selected Waypoint</Typography>
        <PillButton onClick={() => onDeleteWaypoint(selectedWaypoint)} text="Delete" btnStyle="delete" />
        <CancelOutlinedIcon style={{ cursor: 'pointer' }} fontSize="large" onClick={closeFormHandler} />
      </div>
      <form>
        <div className={`${classes.selectContainer} mb-3`}>
          <FormLabel>Type</FormLabel>
          <Select
            className={classes.selectInput}
            styles={selectStyles}
            name="type"
            options={typeListOptions}
            value={type}
            onChange={changeTypeSelectHandler}
          />
        </div>

        { selectedWaypoint.type === Constants.WaypointType.Store && (
        <>
          <div className={`${classes.selectContainer} mb-3`}>
            <FormLabel>Name of shop in mall’s directory</FormLabel>
            <Select
              className={classes.selectInput}
              styles={selectStyles}
              name="mallDirectory"
              options={mallDirectoryListOptions}
              value={mallDirectory}
              onChange={changeMallDirectorySelectHandler}
            />
          </div>

          { !IsEmpty(mallDirectory) && !IsEmpty(mallDirectory.unitNo) && (
            <div className={`${classes.selectContainer} mb-3`}>
              <FormLabel>Unit No.</FormLabel>
              <Typography>{mallDirectory.unitNo}</Typography>
            </div>
          ) }

          <div className={`${classes.selectContainer} mb-3`}>
            <FormLabel>Page</FormLabel>
            <Select
              className={classes.selectInput}
              styles={selectStyles}
              name="page"
              options={pageListOptions}
              value={page}
              onChange={changePageSelectHandler}
              isClearable
            />
          </div>

          <div className={`${classes.selectContainer} mb-3`}>
            <FormLabel>Page’s Outlet</FormLabel>
            <Select
              className={classes.selectInput}
              styles={selectStyles}
              name="outlet"
              options={outletListOptions}
              value={outlet}
              onChange={changeOutletSelectHandler}
              isClearable
            />
          </div>

          { isWaypointTextVisible && (
            <div className={`${classes.selectContainer} mb-3`}>
              <FormLabel>Text Rotation</FormLabel>
              <div>
                <Input
                  id="exampleRange"
                  name="range"
                  type="range"
                  value={textRotation}
                  onChange={({ target }) => changeTextRotationHandler(target.value)}
                  step={1}
                  min={-180}
                  max={180}
                  onBlur={() => setSelectedWaypointLabel(null)}
                />
                <TextField
                  className={classes.textInput}
                  variant="outlined"
                  value={textRotation}
                  onChange={({ target }) => changeTextRotationHandler(target.value)}
                  type="number"
                  InputProps={{ inputProps: { step: 1, min: -180, max: 180 } }}
                  onBlur={onBlurTextRotationHandler}
                />
              </div>
            </div>
          )}
        </>
        )}
      </form>
    </>
  );
};

MallNavigationForm.propTypes = {
  pageListOptions:          PropTypes.arrayOf(PropTypes.shape(Object)),
  mallDirectoryListOptions: PropTypes.arrayOf(PropTypes.shape(Object)),
  selectedWaypoint:         PropTypes.shape(Object),
  onUpdateWaypoint:         PropTypes.func,
  onDeleteWaypoint:         PropTypes.func,
};

MallNavigationForm.defaultProps = {
  pageListOptions:          [],
  mallDirectoryListOptions: [],
  selectedWaypoint:         {},
  onDeleteWaypoint:         () => {},
  onUpdateWaypoint:         () => {},
};

export default MallNavigationForm;
